import * as THREE from 'three';
import {AbstractPeriodicIntersectionSolver} from './AbstractPeriodicIntersectionSolver';

/**
 * 二次三角方程系数。
 *
 * 系数顺序对应：
 * `A cos²θ + B cosθsinθ + C sin²θ + D cosθ + E sinθ + F = 0`。
 */
export type TrigonometricEquationCoefficients = readonly [number, number, number, number, number, number];

/**
 * 二次三角方程求解结果。
 */
export interface TrigonometricEquationRootResult {
    /** 方程是否在整个周期恒等成立。 */
    isIdentical: boolean;
    /** `[0,2π)` 内全部去重后的实根。 */
    angles: number[];
}

/**
 * 二次三角方程真实根求解器。
 *
 * 职责：
 * 1. 将二次三角方程通过 `tan(θ/2)` 转换为四次多项式；
 * 2. 求解四次及以下实系数多项式的全部实根；
 * 3. 补检半角参数无法表达的 `θ=π` 和周期接缝；
 * 4. 通过原始方程残差过滤、排序和去重候选根。
 */
export class TrigonometricEquationRootSolver extends AbstractPeriodicIntersectionSolver {
    /** 多项式降阶和实根判定容差。 */
    private static readonly POLYNOMIAL_EPSILON = 1e-12;

    /** 原始三角方程相对残差容差。 */
    private static readonly EQUATION_RELATIVE_TOLERANCE = 1e-10;

    /**
     * 求解一个完整周期内的全部根。
     * @param coefficients 二次三角方程系数。
     * @returns 恒等关系和全部唯一角根。
     */
    protected solveTrigonometricEquation(
        coefficients: TrigonometricEquationCoefficients,
    ): TrigonometricEquationRootResult {
        const coefficientScale = Math.max(1, ...coefficients.map((coefficient) => Math.abs(coefficient)));
        const normalizedCoefficients = coefficients.map(
            (coefficient) => coefficient / coefficientScale,
        ) as unknown as TrigonometricEquationCoefficients;
        const quarticCoefficients = this.buildTangentHalfAngleQuarticCoefficients(normalizedCoefficients);

        if (quarticCoefficients.every(
            (coefficient) => Math.abs(coefficient)
                <= TrigonometricEquationRootSolver.EQUATION_RELATIVE_TOLERANCE,
        )) {
            return {isIdentical: true, angles: []};
        }

        const angleCandidates = this.solveRealPolynomialRoots(quarticCoefficients)
            .map((root) => this.normalizeAngle(2 * Math.atan(root)));

        // 半角参数在 π 处无穷，同时显式补检 0 可稳定周期接缝根。
        [0, Math.PI].forEach((angle) => {
            if (this.isAngleEquationSatisfied(normalizedCoefficients, angle)) {
                angleCandidates.push(angle);
            }
        });

        const angles: number[] = [];
        angleCandidates
            .map((angle) => this.normalizeAngle(angle))
            .filter((angle) => this.isAngleEquationSatisfied(normalizedCoefficients, angle))
            .sort((left, right) => left - right)
            .forEach((angle) => {
                if (angles.some((candidate) => this.resolvePeriodicAngleDistance(candidate, angle) <= 1e-9)) {
                    return;
                }
                angles.push(angle);
            });

        return {isIdentical: false, angles};
    }

    /**
     * 将三角方程转换为半角参数四次多项式。
     * @param coefficients 二次三角方程系数。
     * @returns 按降幂排列的四次多项式系数。
     */
    private buildTangentHalfAngleQuarticCoefficients(
        coefficients: TrigonometricEquationCoefficients,
    ): readonly [number, number, number, number, number] {
        const [a, b, c, d, e, f] = coefficients;
        return [
            a - d + f,
            -2 * b + 2 * e,
            -2 * a + 4 * c + 2 * f,
            2 * b + 2 * e,
            a + d + f,
        ];
    }

    /**
     * 判断候选角是否满足原始方程。
     * @param coefficients 二次三角方程系数。
     * @param angle 候选角。
     * @returns 残差满足相对容差时返回 true。
     */
    private isAngleEquationSatisfied(
        coefficients: TrigonometricEquationCoefficients,
        angle: number,
    ): boolean {
        const residual = Math.abs(this.evaluateAngleEquation(coefficients, angle));
        const scale = Math.max(1, ...coefficients.map((coefficient) => Math.abs(coefficient)));
        return residual <= scale * TrigonometricEquationRootSolver.EQUATION_RELATIVE_TOLERANCE;
    }

    /**
     * 计算原始三角方程值。
     * @param coefficients 二次三角方程系数。
     * @param angle 当前角。
     * @returns 方程值。
     */
    private evaluateAngleEquation(
        coefficients: TrigonometricEquationCoefficients,
        angle: number,
    ): number {
        const cosine = Math.cos(angle);
        const sine = Math.sin(angle);
        const [a, b, c, d, e, f] = coefficients;
        return a * cosine * cosine
            + b * cosine * sine
            + c * sine * sine
            + d * cosine
            + e * sine
            + f;
    }

    /**
     * 求解四次及以下实系数多项式的全部实根。
     * @param coefficients 按降幂排列的系数。
     * @returns 全部有限实根。
     */
    private solveRealPolynomialRoots(coefficients: readonly number[]): number[] {
        const trimmedCoefficients = [...coefficients];
        while (
            trimmedCoefficients.length > 0
            && Math.abs(trimmedCoefficients[0]) <= TrigonometricEquationRootSolver.POLYNOMIAL_EPSILON
        ) {
            trimmedCoefficients.shift();
        }

        const degree = trimmedCoefficients.length - 1;
        if (degree <= 0) {
            return [];
        }
        if (degree === 1) {
            return [-trimmedCoefficients[1] / trimmedCoefficients[0]];
        }
        if (degree === 2) {
            return this.solveQuadraticRealRoots(...trimmedCoefficients as [number, number, number]);
        }
        if (degree === 3) {
            return this.solveCubicRealRoots(...trimmedCoefficients as [number, number, number, number]);
        }
        if (degree === 4) {
            return this.solveQuarticRealRoots(...trimmedCoefficients as [number, number, number, number, number]);
        }

        console.error(`[TrigonometricEquationRootSolver] 仅支持四次及以下多项式，收到 ${degree} 次方程。`);
        return [];
    }

    /**
     * 求解二次方程全部实根。
     * @param a 二次项系数。
     * @param b 一次项系数。
     * @param c 常数项。
     * @returns 全部实根。
     */
    private solveQuadraticRealRoots(a: number, b: number, c: number): number[] {
        const epsilon = TrigonometricEquationRootSolver.POLYNOMIAL_EPSILON;
        if (Math.abs(a) <= epsilon) {
            return Math.abs(b) <= epsilon ? [] : [-c / b];
        }
        const discriminant = b * b - 4 * a * c;
        const tolerance = Math.max(1, b * b, Math.abs(4 * a * c)) * epsilon;
        if (discriminant < -tolerance) {
            return [];
        }
        if (Math.abs(discriminant) <= tolerance) {
            return [-b / (2 * a)];
        }
        const squareRoot = Math.sqrt(discriminant);
        return [(-b - squareRoot) / (2 * a), (-b + squareRoot) / (2 * a)];
    }

    /**
     * 求解三次方程全部实根。
     * @param a 三次项系数。
     * @param b 二次项系数。
     * @param c 一次项系数。
     * @param d 常数项。
     * @returns 全部实根。
     */
    private solveCubicRealRoots(a: number, b: number, c: number, d: number): number[] {
        const epsilon = TrigonometricEquationRootSolver.POLYNOMIAL_EPSILON;
        if (Math.abs(a) <= epsilon) {
            return this.solveQuadraticRealRoots(b, c, d);
        }
        const normalizedB = b / a;
        const normalizedC = c / a;
        const normalizedD = d / a;
        const p = normalizedC - normalizedB * normalizedB / 3;
        const q = 2 * normalizedB ** 3 / 27 - normalizedB * normalizedC / 3 + normalizedD;
        const discriminant = q * q / 4 + p ** 3 / 27;
        const shift = normalizedB / 3;
        if (discriminant > epsilon) {
            const squareRoot = Math.sqrt(discriminant);
            return [
                Math.cbrt(-q / 2 + squareRoot) + Math.cbrt(-q / 2 - squareRoot) - shift,
            ];
        }
        if (Math.abs(discriminant) <= epsilon) {
            const shared = Math.cbrt(-q / 2);
            return this.dedupeNumericRoots([2 * shared - shift, -shared - shift]);
        }
        const magnitude = 2 * Math.sqrt(-p / 3);
        const phase = Math.acos(THREE.MathUtils.clamp(
            (-q / 2) / Math.sqrt(-(p ** 3) / 27),
            -1,
            1,
        ));
        return this.dedupeNumericRoots([
            magnitude * Math.cos(phase / 3) - shift,
            magnitude * Math.cos((phase + Math.PI * 2) / 3) - shift,
            magnitude * Math.cos((phase + Math.PI * 4) / 3) - shift,
        ]);
    }

    /**
     * 求解四次方程全部实根。
     * @param a 四次项系数。
     * @param b 三次项系数。
     * @param c 二次项系数。
     * @param d 一次项系数。
     * @param e 常数项。
     * @returns 全部实根。
     */
    private solveQuarticRealRoots(a: number, b: number, c: number, d: number, e: number): number[] {
        const epsilon = TrigonometricEquationRootSolver.POLYNOMIAL_EPSILON;
        if (Math.abs(a) <= epsilon) {
            return this.solveCubicRealRoots(b, c, d, e);
        }
        const normalizedB = b / a;
        const normalizedC = c / a;
        const normalizedD = d / a;
        const normalizedE = e / a;
        const p = normalizedC - 3 * normalizedB * normalizedB / 8;
        const q = normalizedD - normalizedB * normalizedC / 2 + normalizedB ** 3 / 8;
        const r = normalizedE
            - normalizedB * normalizedD / 4
            + normalizedB * normalizedB * normalizedC / 16
            - 3 * normalizedB ** 4 / 256;

        if (Math.abs(q) <= epsilon) {
            const roots = this.solveQuadraticRealRoots(1, p, r).flatMap((value) => {
                if (value < -epsilon) {
                    return [];
                }
                const magnitude = Math.sqrt(Math.max(0, value));
                return magnitude <= epsilon ? [0] : [-magnitude, magnitude];
            });
            return this.filterQuarticRoots(
                roots.map((root) => root - normalizedB / 4),
                [a, b, c, d, e],
            );
        }

        const roots: number[] = [];
        this.solveCubicRealRoots(1, 2 * p, p * p - 4 * r, -q * q).forEach((z) => {
            if (z < -epsilon) {
                return;
            }
            const s = Math.sqrt(Math.max(0, z));
            if (s <= epsilon) {
                return;
            }
            const t = (p + z - q / s) / 2;
            const u = (p + z + q / s) / 2;
            roots.push(
                ...this.solveQuadraticRealRoots(1, s, t),
                ...this.solveQuadraticRealRoots(1, -s, u),
            );
        });
        return this.filterQuarticRoots(
            roots.map((root) => root - normalizedB / 4),
            [a, b, c, d, e],
        );
    }

    /**
     * 回代过滤四次方程候选根。
     * @param roots 候选根。
     * @param coefficients 原始多项式系数。
     * @returns 通过相对残差校验的唯一实根。
     */
    private filterQuarticRoots(roots: readonly number[], coefficients: readonly number[]): number[] {
        const coefficientScale = Math.max(1, ...coefficients.map((coefficient) => Math.abs(coefficient)));
        return this.dedupeNumericRoots(roots.filter((root) => {
            const residualScale = coefficientScale * Math.max(1, Math.abs(root)) ** (coefficients.length - 1);
            return Math.abs(this.evaluatePolynomial(coefficients, root))
                <= residualScale * TrigonometricEquationRootSolver.EQUATION_RELATIVE_TOLERANCE;
        }));
    }

    /**
     * 使用 Horner 法计算多项式值。
     * @param coefficients 多项式系数。
     * @param value 自变量。
     * @returns 多项式值。
     */
    private evaluatePolynomial(coefficients: readonly number[], value: number): number {
        return coefficients.reduce((result, coefficient) => result * value + coefficient, 0);
    }

    /**
     * 对数值根排序并去重。
     * @param roots 候选根。
     * @returns 唯一有限实根。
     */
    private dedupeNumericRoots(roots: readonly number[]): number[] {
        const results: number[] = [];
        roots.filter(Number.isFinite).sort((left, right) => left - right).forEach((root) => {
            if (!results.some((candidate) => Math.abs(candidate - root) <= 1e-9)) {
                results.push(root);
            }
        });
        return results;
    }

    /**
     * 计算两个周期角之间的最短距离。
     * @param first 第一个角。
     * @param second 第二个角。
     * @returns 最短弧度距离。
     */
    private resolvePeriodicAngleDistance(first: number, second: number): number {
        const distance = Math.abs(first - second);
        return Math.min(distance, Math.PI * 2 - distance);
    }
}
